import { WppSpinner } from '@wppopen/components-library-react'
import { useOs } from '@wppopen/react'
import clsx from 'clsx'
import { jwtDecode } from 'jwt-decode'
import { useEffect, PropsWithChildren, useMemo, useState, useContext, createContext } from 'react'

import { briefApi, mtApi } from 'api'
import { ApiInstance } from 'api/common/createApi'
import styles from 'assets/css/main.module.scss'
import { Flex } from 'components/common/flex/Flex'
import config, { API_TYPES } from 'constants/api-config'
import { isTokenExpired } from 'utils'

const AZ_TOKEN_KEY = 'WPP_ALPHA_ZULU_TOKEN'
const apiInstances = [briefApi, mtApi]

const UserRoleContext = createContext<any>(undefined)

const getInterceptorToEject = (instance: ApiInstance, token: string) => {
  return instance.client.interceptors.request.use(
    instanceConfig => {
      instanceConfig!.headers!.Authorization = `Bearer ${token}`

      instanceConfig!.headers!['Client'] = 'playground-dev-fe'
      instanceConfig!.headers!['X-Tenant-Id'] = '571f9aa5-bb8b-4f9f-8835-23e74b64e050'

      return instanceConfig
    },
    error => Promise.reject(error),
  )
}

export const ApiProvider = ({ children }: PropsWithChildren<{}>) => {
  const [azToken, setAzToken] = useState<string | null>(null)
  const { osApi } = useOs()
  const wppToken = osApi.getAccessToken()
  const [interceptorsDone, setInterceptorsDone] = useState(false)
  const [maxRetriesReached, setMaxRetriesReached] = useState(false)
  const [userRole, setUserRole] = useState<any>(null)

  const setAndSaveAZToken = (token: string) => {
    if (token === 'undefined' || !token) {
      localStorage.removeItem(AZ_TOKEN_KEY)
      setAzToken(null)
      return
    }
    localStorage.setItem(AZ_TOKEN_KEY, token)
    setAzToken(token)
  }

  const fetchAZTokenWithRetry = async (attempt = 1) => {
    if (!wppToken) {
      console.warn('No user access token available. Skipping AZ token fetch.')
      return
    }

    const MAX_RETRIES = 4
    const RETRY_DELAY = 1000 // Start with 1 second

    try {
      const res = await fetch(`${config[API_TYPES.MT_API_URL]}/alpha-zulu/token`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          client: 'playground-dev-fe',
        },
        body: JSON.stringify({
          token: wppToken,
        }),
      })

      if (!res.ok) {
        throw new Error(`HTTP error! Status: ${res.status}`)
      }

      const data = await res.json()
      setAndSaveAZToken(data.accessToken)
    } catch (error) {
      console.error(`Error fetching token (Attempt ${attempt}/${MAX_RETRIES}):`, error)

      if (attempt < MAX_RETRIES) {
        const delay = RETRY_DELAY * Math.pow(2, attempt - 1) // Exponential backoff
        setTimeout(() => fetchAZTokenWithRetry(attempt + 1), delay)
      } else {
        console.error('Max retry attempts reached. Unable to fetch AZ token.')
        localStorage.removeItem(AZ_TOKEN_KEY)
        setMaxRetriesReached(true)
      }
    }
  }

  useEffect(() => {
    let interceptors: any[] = []
    if (azToken) {
      interceptors = apiInstances.map((instance, index) => {
        const interceptorToEject = getInterceptorToEject(instance, index === 0 ? azToken : wppToken)
        if (index === 0) {
          instance.client.interceptors.response.use(
            response => response,
            error => {
              try {
                if (!azToken && wppToken) {
                  setAzToken('')
                  localStorage.removeItem(AZ_TOKEN_KEY)
                  fetchAZTokenWithRetry()
                  error.config._retry = true
                  return instance.client(error.config)
                }

                const decodedToken = jwtDecode(azToken)
                setUserRole(decodedToken)
                if (error.response?.status === 401 && isTokenExpired(decodedToken?.exp) && wppToken) {
                  setAzToken('')
                  localStorage.removeItem(AZ_TOKEN_KEY)
                  fetchAZTokenWithRetry()
                  error.config._retry = true
                  return instance.client(error.config)
                }
              } catch (error) {
                console.error('Error handling response', error)
              }
              return Promise.reject(error)
            },
          )
        }
        return interceptorToEject
      })
      setInterceptorsDone(true)
    } else {
      fetchAZTokenWithRetry()
      return
    }

    return () => {
      interceptors.forEach((interceptor, index) => {
        apiInstances[index].client.interceptors.request.eject(interceptor)
      })
    }
    // eslint-disable-next-line
  }, [azToken])

  const isReady = useMemo(() => {
    if (!!azToken && !!wppToken && interceptorsDone) {
      return true
    }
    if (maxRetriesReached) {
      return true
    }
    if (!azToken || !wppToken || !interceptorsDone) {
      return false // TODO: Add loading spinner
    }
    return true
  }, [azToken, wppToken, interceptorsDone, maxRetriesReached])

  if (!isReady) {
    return (
      <Flex justify="center" align="center" className={clsx(styles.wFull, styles.minHScreen)}>
        <WppSpinner size="l" data-testid="spinner" />
      </Flex>
    )
  }

  return <UserRoleContext.Provider value={userRole}>{isReady && children}</UserRoleContext.Provider>
}

export const useUserRole = () => useContext(UserRoleContext)
